import { Component, OnInit, inject, NgZone, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser, ViewportScroller } from '@angular/common';
import { trigger, style, animate, transition } from '@angular/animations';
import { Router, RouterOutlet, NavigationEnd, ActivationEnd } from '@angular/router';
import { NgxUiLoaderModule } from 'ngx-ui-loader';
import { take } from 'rxjs/operators';

import { GlobalService } from 'src/app/services/global.service';
import { SEOService } from 'src/app/services/seo.service';
import { ScriptService } from 'src/app/services/script.service';
import { PosthogService } from 'src/app/services/posthog.service';
import { SentryService } from 'src/app/services/sentry.service';
import { SessionRecordingService } from 'src/app/services/session-recording.service';
import { CustomerService } from 'src/app/services/customer.service';

import { environment } from 'src/environments/environment';
import { ecommerceConstants } from 'src/app/shared/ecommerce-constants';

declare let Cookiebot: any;

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  animations: [
    trigger('outAnimation', [
      transition(':leave', [style({ opacity: 1 }), animate('0.3s ease-in', style({ height: 0, opacity: 0 }))]),
    ]),
  ],
  standalone: true,
  imports: [RouterOutlet, NgxUiLoaderModule],
})
export class AppComponent implements OnInit {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly router = inject(Router);
  private readonly viewportScroller = inject(ViewportScroller);
  private readonly ngZone = inject(NgZone);

  globalService = inject(GlobalService);
  seoService = inject(SEOService);
  private readonly scriptService = inject(ScriptService);
  private readonly posthogService = inject(PosthogService);
  private readonly sentryService = inject(SentryService);
  private readonly sessionRecordingService = inject(SessionRecordingService);
  private readonly customerService = inject(CustomerService);

  constants = ecommerceConstants;

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId) && 'scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    this.posthogService.init();
    this.sentryService.init();
    this.sessionRecordingService.init();

    // Identify user in PostHog and Sentry when auth state changes
    this.customerService.loggedCustomer$.subscribe((customer) => {
      if (customer) {
        this.posthogService.identify(String(customer.id), {
          email: customer.emailLogin,
          name: customer.name,
          country: customer.country?.code,
          salesforceId: customer.salesforceId,
          role: customer.role,
        });
        this.sentryService.setUser({
          id: String(customer.id),
          email: customer.emailLogin,
          username: customer.name,
        });
      } else {
        this.posthogService.reset();
        this.sentryService.setUser(null);
      }
    });

    this.globalService.logInfo('Glovo Store version', environment.appVersion);

    this.router.events.subscribe((e) => {
      if (e instanceof ActivationEnd) {
        if (e.snapshot?.data.hotjar && e.snapshot?.routeConfig?.path) {
          this.seoService.trackHotjarUrl(this.router.routerState.snapshot.url);
        }
      }

      if (e instanceof NavigationEnd) {
        this.scrollToTopIOSSafe();

        if (this.isSpecialUrl(this.router)) {
          return;
        }

        const mergedParams = this.mergeRouteParams(this.router);
        this.globalService.logInfo('mergedParams', mergedParams);

        if (mergedParams.countryCode && mergedParams.locale) {
          const country = this.globalService.isAvailableCountryCode(mergedParams.countryCode);
          if (country && country.locale === mergedParams.locale) {
            this.globalService.setParams({
              countryCode: country.code,
              locale: country.locale,
              extraCurrencySymbol: country.extraCurrencySymbol,
              extraConversion: country.extraConversion,
            });
            this.seoService.updateLang(country.locale);
            this.seoService.loadSchemaJsonLd(country.code);
            this.loadScripts(country);
            this.loadCookieBotLanguage(country.locale);
          }
        }
      }
    });
  }

  private scrollToTopIOSSafe(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    const doScroll = () => {
      this.viewportScroller.scrollToPosition([0, 0]);
      window.scrollTo(0, 0);
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };

    doScroll();

    requestAnimationFrame(() => {
      doScroll();

      setTimeout(() => doScroll(), 0);
      setTimeout(() => doScroll(), 50);
      setTimeout(() => doScroll(), 150);
      setTimeout(() => doScroll(), 300);
    });

    this.ngZone.onStable.pipe(take(1)).subscribe(() => {
      doScroll();
      setTimeout(() => doScroll(), 50);
    });
  }

  isSpecialUrl(router: Router): boolean {
    const routeUrl = router.routerState.snapshot.url;
    const routeUrlParts = routeUrl.split('/');
    return routeUrlParts[1] && routeUrlParts[1].startsWith('error');
  }

  mergeRouteParams(router: Router): { countryCode?: string; locale?: string } {
    const params: any = {};
    const routeUrl = router.routerState.snapshot.url;
    const routeUrlParts = routeUrl.split('/');

    if (routeUrlParts[1]) {
      params.countryCode = routeUrlParts[1];
    }

    if (routeUrlParts[2]) {
      params.locale = routeUrlParts[2];
    }

    return params;
  }

  onActivate() {
    if (isPlatformBrowser(this.platformId)) {
      window.scroll(0, 0);
    }
    // Si estás en iframe o prerender, no fuerces SEO dinámico
    if (this.globalService.inIframe?.() || this.globalService.isPrerender?.()) {
      return;
    }

    // Canonical and Hrefland by default
    const path = (this.router.url || '').split('?')[0].split('#')[0];
    if (path === '/' || path === '') {
      this.seoService.updateCanonicalUrl();
      this.seoService.loadHreflang('home');
    }
  }

  loadCookieBotLanguage(locale?: string) {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    const cookieBotElement = document.getElementById('Cookiebot');
    if (!cookieBotElement) {
      return;
    }
    cookieBotElement.setAttribute('data-culture', locale);
  }

  loadScripts(country: any) {
    if (!isPlatformBrowser(this.platformId) || this.globalService.inIframe() || this.globalService.isPrerender()) {
      return;
    }
    // Mandatory scripts
    // TODO: PageSpeed Insights - Volver a activar ProcessOut al inicio?
    // this.processoutService.init();

    // Doofinder — defer until user interacts with search (237KB script, 91% unused on page load)
    if (country.doofinderActive) {
      const searchInput = document.getElementById('search-input');
      if (searchInput) {
        const loadDoofinder = () => {
          this.scriptService
            .load('doofinder')
            .then()
            .catch((error) => console.error(error));
          searchInput.removeEventListener('focus', loadDoofinder);
          searchInput.removeEventListener('click', loadDoofinder);
        };
        searchInput.addEventListener('focus', loadDoofinder, { once: true });
        searchInput.addEventListener('click', loadDoofinder, { once: true });
      }
    }

    // Optional scripts
    window.addEventListener(
      'CookiebotOnAccept',
      () => {
        // Cookiebot.consent.preferences
        // Cookiebot.consent.statistics
        // Cookiebot.consent.marketing
        if (Cookiebot.consent.statistics) {
          // TODO: PageSpeed Insights - Volver a activar Hotjar?
          // this.seoService.loadHotjar();
        }
      },
      false
    );
  }
}
